import { useLingui } from '@lingui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import Button from '../components/Button';
import Icon from '../components/Icon';

interface TeamMemberSummary {
  id: string;
  name: string;
  role: { level: string; jobGroup: string; jobTitle: string } | null;
  expirationDate?: string;
}

/**
 * Displays a list of all saved team members with options to edit or delete each entry.
 */
export default function TeamOverviewPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMemberSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMembers();
  }, []);

  async function loadMembers(): Promise<void> {
    setLoading(true);
    try {
      const { data } = await axios.get<{ files: string[] }>(
        'http://localhost:3001/files',
      );

      const teamFiles = data.files.filter((f) => f.startsWith('team/'));

      const memberPromises = teamFiles.map((file) =>
        axios
          .get<TeamMemberSummary>(`http://localhost:3001/files/${file}`)
          .then((res) => res.data),
      );

      setMembers(await Promise.all(memberPromises));
    } catch (err) {
      console.error('Failed to load team members', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    try {
      await axios.delete(`http://localhost:3001/files/team/${id}.json`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      console.error('Failed to delete member', err);
    }
  }

  if (loading) {
    return (
      <div className="container-page padding-top-8">
        <p>{i18n._('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-8">
        <div className="flex justify-between items-center margin-bottom-4">
          <h1>{i18n._('team.title')}</h1>
          <Button onClick={() => navigate('/team/new')}>
            {i18n._('team.new')}
          </Button>
        </div>

        {members.length === 0 && (
          <p>{i18n._('team.empty')}</p>
        )}

        <div className="flex flex-col gap-2">
          {members.map((member) => (
            <div
              key={member.id}
              className="border border-gray-tint-6 rounded-_20 padding-3 flex justify-between items-center"
            >
              <div>
                <strong>{member.name}</strong>
                {member.role && (
                  <span className="margin-left-2 text-sm">
                    {member.role.jobTitle} · {member.role.jobGroup}
                  </span>
                )}
                {member.expirationDate && (
                  <span className="margin-left-2 text-sm text-gray">
                    (bis {member.expirationDate})
                  </span>
                )}
              </div>

              <div className="flex gap-1">
                <Icon
                  name="icon-edit"
                  size={2}
                  onClick={() => navigate(`/team/edit/${member.id}`)}
                />
                <Icon
                  name="icon-bin"
                  size={2}
                  onClick={() => handleDelete(member.id)}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
